
import { CustomError, ICustomErrorProps } from '@ms/utilities-error/lib/index';
import { Signal } from './Signal';

/**
 * @public
 */
export interface ICancelErrorProps extends ICustomErrorProps {
    // Nothing.
}

/**
 * Error type used to represent cancellation of a task or promise.
 * Throw an instance of this Error to indicate that a function or async operation
 * has terminated because it was asked to be canceled, and did so gracefully.
 * @public
 */
export class CancelError extends CustomError {
    constructor(props: ICancelErrorProps = {
        message: 'Canceled'
    }) {
        super(props);

        this.name = 'Canceled';
    }
}

/**
 * Determines whether or not an error represents cancellation.
 * @deprecated Use CancelError.matches instead.
 * @public
 */
export function isCancel(error: Error): error is CancelError {
    return error instanceof CancelError;
}

/**
 * Implementation of a cancellation token, which may be tested within a synchronous or
 * asynchronous task to determine if that task should be canceled.
 * @public
 */
export class CancelToken {
    /**
     * Subscribes a callback to the token which will be invoked if the token is put
     * into a canceled state. This function returns a function which should be
     * used to unsubscribe from the token when the task is complete or may no longer
     * be canceled.
     */
    public readonly subscribe: (onCanceled: () => void) => (() => void);

    /**
     * Determines whether or not the token is in a canceled state.
     * Note: a CancelToken can never be in a canceled state synchronously after
     * it has been created, regardless of whether its input was already rejected.
     */
    public readonly isCanceled: () => boolean;

    /**
     * Throws a CancelError if
     */
    public readonly throwIfCanceled: () => void;

    /**
     * Constructs a new CancelToken.
     * @param canceledOnReject - A Promise which, when resolved, disposes the token without invoking
     * any cancellation callbacks, or which, when rejected, puts the token into a canceled state
     * and invoked any cancellation callbacks.
     *
     * In general, use CancelSource to get an instance of a CancelToken.
     */
    constructor(canceledOnReject: Promise<void>) {
        let cancelError: Error;

        const afterReject = canceledOnReject.catch((error: Error) => {
            cancelError = error;

            return Promise.reject(error);
        });

        this.subscribe = (onCanceled: () => void) => {
            let callback: typeof onCanceled | undefined = onCanceled;

            afterReject.then(() => {
                callback = undefined;
            }, (error: Error) => {
                if (callback) {
                    const finalCallback = callback;

                    callback = undefined;

                    finalCallback();
                }
            });

            return () => {
                callback = undefined;
            };
        };

        this.isCanceled = () => !!cancelError;

        this.throwIfCanceled = () => {
            if (cancelError) {
                throw new CancelError(cancelError);
            }
        };
    }
}

/**
 * Component which acts as the source for a CancelToken, controlling
 * when it is canceled.
 * @public
 */
export class CancelSource {
    /**
     * The token managed by this source.
     */
    public readonly token: CancelToken;

    /**
     * Cancels the token.
     */
    public readonly cancel: (error?: Error) => void;

    /**
     * Disposes the source, indicating that the token can no longer be canceled.
     */
    public readonly dispose: () => void;

    constructor() {
        const {
            reject,
            resolve,
            promise
        } = new Signal<void>();

        this.cancel = () => reject(new Error());
        this.dispose = () => resolve();

        this.token = new CancelToken(promise);
    }
}

/**
 * Races between a pending promise and cancellation.
 * @beta
 */
export async function raceWithCancel<TResult>(
    promise: Promise<TResult>,
    cancelToken?: CancelToken
): Promise<TResult> {
    if (!cancelToken) {
        return await promise;
    }

    const signal = new Signal<TResult>();

    const unsubscribe = cancelToken.subscribe(() => {
        signal.reject(new CancelError());
    });

    (async () => {
        try {
            signal.resolve(await promise);
        } catch (e) {
            signal.reject(e);
        } finally {
            unsubscribe();
        }
    })();

    return await signal.promise;
}
